import { localXY } from "./contextPlanes";
import { fitsFootprint } from "./siteGeometry";

const R = 6371000;
const rad = Math.PI / 180;
const xy = p => ({ x: p[0], y: p[1] });
const pair = p => [p.x, p.y];
const midpoint = t => ({ x: (t.start[0] + t.end[0]) / 2, y: (t.start[1] + t.end[1]) / 2 });
export const rowDirection = azimuth => ({ x: Math.sin((azimuth - 90) * rad), y: Math.cos((azimuth - 90) * rad) });
const project = (p, u) => p.x * u.x + p.y * u.y;

export function rowFootprint(t, depth) {
  const dx = t.end[0] - t.start[0], dy = t.end[1] - t.start[1];
  const length = Math.hypot(dx, dy);
  if (!length) return [];
  const c = { x: dy / length * depth / 2, y: -dx / length * depth / 2 };
  return [t.start, t.end].map(p => ({ x: p[0] + c.x, y: p[1] + c.y }))
    .concat([t.end, t.start].map(p => ({ x: p[0] - c.x, y: p[1] - c.y })));
}

// Store an explicit, fixed origin. Recentering after every drag would move
// every other row relative to the obstacles, the satellite and saved shading.
export function groundLayoutFromRows(rows, { origin, boundary = [], usable = [] }) {
  return {
    tables: rows.map((r, i) => ({ label: r.label || `R${i + 1}`, columns: r.modules,
      start: [(r.low[0].x + r.high[0].x) / 2, (r.low[0].y + r.high[0].y) / 2],
      end: [(r.low[1].x + r.high[1].x) / 2, (r.low[1].y + r.high[1].y) / 2] })),
    outline: { boundary_xy: boundary.map(pair), usable_xy: usable.map(pair),
      frame_origin_xy: [0, 0], frame_origin_lonlat: origin || null },
  };
}

export function geographicBoundary(ring, origin) {
  return (ring || []).map(p => localXY(p, origin)).filter(Boolean);
}

export function rowPitchStats(tables, azimuth) {
  const u = rowDirection(azimuth), c = { x: u.y, y: -u.x };
  const positions = [...new Set(tables.map(t => Math.round(project(midpoint(t), c) * 1000) / 1000))].sort((a, b) => a - b);
  const gaps = positions.slice(1).map((p, i) => p - positions[i]);
  return gaps.length ? { min: Math.min(...gaps), max: Math.max(...gaps),
    mean: gaps.reduce((a, b) => a + b, 0) / gaps.length, variable: Math.max(...gaps) - Math.min(...gaps) > 0.01 } : null;
}

export function groundFieldArea(tables, depth, azimuth, marginFactor = 1) {
  if (!tables?.length || !(depth > 0)) return null;
  const u = rowDirection(azimuth), c = { x: u.y, y: -u.x };
  const points = tables.flatMap(t => rowFootprint(t, depth));
  const along = points.map(p => project(p, u)), across = points.map(p => project(p, c));
  return (Math.max(...along) - Math.min(...along)) * (Math.max(...across) - Math.min(...across)) * Math.max(1, Number(marginFactor) || 1);
}

function overlaps(a, b) {
  // Separating-axis test: touching edges are allowed, overlapping tables aren't.
  return [a, b].every(poly => poly.every((p, i) => {
    const q = poly[(i + 1) % poly.length], n = { x: q.y - p.y, y: p.x - q.x };
    const aa = a.map(v => project(v, n)), bb = b.map(v => project(v, n));
    return Math.min(Math.max(...aa), Math.max(...bb)) - Math.max(Math.min(...aa), Math.min(...bb)) > 1e-7;
  }));
}

export function groundRowsValid(layout, depth, setback = 0, changedIndex = null) {
  const boundary = layout.outline?.boundary_xy?.map(xy) || [];
  const quads = layout.tables.map(t => rowFootprint(t, depth));
  return layout.tables.every((t, i) => {
    if (changedIndex != null && i !== changedIndex) return true;
    return Number.isInteger(t.columns) && t.columns > 0 && quads[i].length === 4
      && quads[i].every(p => Number.isFinite(p.x) && Number.isFinite(p.y))
      && (boundary.length < 3 || fitsFootprint(quads[i], boundary, Math.max(0, setback)))
      && quads.every((q, j) => i === j || !overlaps(quads[i], q));
  });
}

export function editGroundRow(layout, operation, { moduleWidth, depth, setback = 0, azimuth, pitch }) {
  const tables = layout.tables.map(t => ({ ...t, start: [...t.start], end: [...t.end] }));
  const index = operation.index, t = tables[index];
  if (!(moduleWidth > 0 && depth > 0) || (!t && !["pitch", "move-group"].includes(operation.type))) return null;
  if (operation.type === "move-group") {
    if (![operation.delta?.x, operation.delta?.y].every(Number.isFinite)) return null;
    tables.forEach(row => {
      row.start = [row.start[0] + operation.delta.x, row.start[1] + operation.delta.y];
      row.end = [row.end[0] + operation.delta.x, row.end[1] + operation.delta.y];
    });
  } else if (operation.type === "move") {
    if (![operation.delta?.x, operation.delta?.y].every(Number.isFinite)) return null;
    t.start = [t.start[0] + operation.delta.x, t.start[1] + operation.delta.y];
    t.end = [t.end[0] + operation.delta.x, t.end[1] + operation.delta.y];
  } else if (operation.type === "resize") {
    const u = rowDirection(azimuth);
    const columns = Math.max(1, Math.round(operation.columns));
    if (!Number.isFinite(columns)) return null;
    const length = columns * moduleWidth;
    if (operation.end === "start") t.start = [t.end[0] - u.x * length, t.end[1] - u.y * length];
    else t.end = [t.start[0] + u.x * length, t.start[1] + u.y * length];
    t.columns = columns;
  } else if (operation.type === "remove") {
    if (tables.length < 2) return null;
    tables.splice(index, 1);
  } else if (operation.type === "add") {
    const u = rowDirection(azimuth), c = { x: u.y, y: -u.x };
    const neighbour = tables[index > 0 ? index - 1 : 1];
    const sign = neighbour && project(midpoint(t), c) < project(midpoint(neighbour), c) ? -1 : 1;
    const delta = { x: c.x * pitch * sign, y: c.y * pitch * sign };
    tables.push({ ...t, label: `R${tables.length + 1}`, start: [t.start[0] + delta.x, t.start[1] + delta.y],
      end: [t.end[0] + delta.x, t.end[1] + delta.y] });
  } else if (operation.type === "pitch") {
    if (!(operation.pitch > 0)) return null;
    const u = rowDirection(azimuth), c = { x: u.y, y: -u.x };
    const positions = [...new Set(tables.map(row => Math.round(project(midpoint(row), c) * 1000) / 1000))].sort((a, b) => a - b);
    tables.forEach(row => {
      const old = Math.round(project(midpoint(row), c) * 1000) / 1000;
      const delta = positions[0] + positions.indexOf(old) * operation.pitch - old;
      row.start = [row.start[0] + c.x * delta, row.start[1] + c.y * delta];
      row.end = [row.end[0] + c.x * delta, row.end[1] + c.y * delta];
    });
  } else return null;
  const next = { ...layout, tables };
  if (!groundRowsValid(next, depth, setback, ["move", "resize"].includes(operation.type) ? index : null)) return null;
  return next;
}

export function groundLayoutFields(layout, azimuth) {
  const origin = layout.outline.frame_origin_lonlat;
  const lonlat = p => origin ? [origin[0] + p[0] / (R * Math.cos(origin[1] * rad) * rad), origin[1] + p[1] / (R * rad)] : undefined;
  const stats = rowPitchStats(layout.tables, azimuth);
  return { parcel_tables: layout.tables.map(t => ({ ...t, length_m: Math.hypot(t.end[0] - t.start[0], t.end[1] - t.start[1]),
    start_lonlat: lonlat(t.start), end_lonlat: lonlat(t.end) })), parcel_outline: layout.outline,
    row_modules: layout.tables.map(t => t.columns), row_labels: layout.tables.map(t => t.label), field_rows: String(layout.tables.length),
    ...(stats ? { minimum_distance_m: String(Number(stats.mean.toFixed(6))) } : {}) };
}
